import { FC, useEffect, useState, memo, useCallback } from "react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { getSurveySection } from "app/features/surveySlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { FormControlModel } from "types";
import FormComponentFactory from "lib/factory/FormComponentFactory";
import { YES, NO } from "pages/SurveyForm/constants/uiControls";
import { ElementType } from "lib/enums/enums";
import StepContent from "../StepContent";
import { FieldArray } from "formik";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Button } from "@mui/material";
import FieldTextField from "pages/SurveyForm/components/ui/FieldTextField";
import FieldSelect from "pages/SurveyForm/components/ui/FieldSelect";
import FieldDatePicker from "pages/SurveyForm/components/ui/FieldDatePicker";
import FieldRadioGroup from "pages/SurveyForm/components/ui/FieldRadioGroup";
import FieldCheckboxGroup from "pages/SurveyForm/components/ui/FieldCheckboxGroup";
import { templateChildren, templateChildrenModel } from "pages/SurveyForm/constants/initialValues";
import ErrorMessage from "ui/atoms/ErrorMessage";

type Props = {
  formik?: any;
  sectionId: number;
};

const Step12: FC<Props> = memo(({ formik, sectionId }) => {
  const dispatch = useAppDispatch();
  const { surveySection } = useAppSelector((state) => state.survey) ?? [];
  const { questions = [] } = surveySection || {};
  const [expanded, setExpanded] = useState<number | false>(false);
  const visible = formik?.values?.numberOfChildren?.value >= 1 && formik?.values?.hasChildren?.value === YES;

  const repeatTemplate = (arr: any, repeats: any) => [].concat(...Array.from({ length: repeats }, () => arr));

  useEffect(() => {
    dispatch(getSurveySection(sectionId));
  }, [sectionId]);

  useEffect(() => {
    const children = formik?.values?.childrenRegistered?.value;
    if (children > 0) {
      formik?.setFieldValue("template", repeatTemplate(templateChildrenModel, children), false);
    }
  }, [formik?.values?.childrenRegistered]);

  const clearChildrens = (value: string) => {
    if (value === NO) {
      formik.setFieldValue("template", []);
    }
  };

  const dataModel: Array<FormControlModel> = [
    {
      key: 0,
      formik,
      name: "hasChildren",
      questionData: questions[0],
      label: "¿Tiene hijos?",
      elementType: ElementType.Radio,
      visible: true,
      onChangeCallback: clearChildrens,
    },
    {
      key: 1,
      formik,
      type: "number",
      name: "numberOfChildren",
      questionData: questions[1],
      label: "¿Cuántos hijos tiene?",
      elementType: ElementType.Textfield,
      visible: formik.values.hasChildren?.value === YES,
    },
  ];

  const handleChange = useCallback(
    (panel: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    },
    [setExpanded]
  );

  const clearFieldsConditioned = (index: string, value: string) => {
    if (value === NO) {
      formik.setFieldValue(index, []);
    }
  };

  if (!questions) return <></>;
  return (
    <Container>
      <Typography mb={2} variant='subtitle1' align='center'>
        Cuéntenos sobre su familia
      </Typography>
      <StepContent>
        {dataModel.map(FormComponentFactory)}
        {visible && (
          <Typography mb={2} variant='subtitle1' align='left'>
            Por favor relacione a continuación únicamente la información de sus hijos vivos, que tengan hasta 25 años de
            edad y/o que presenten discapacidad
          </Typography>
        )}
        <FieldArray
          name='template'
          validateOnChange={true}
          render={({ push, remove, form }: any) => {
            return (
              visible && (
                <>
                  {formik?.values?.template?.map((template: any, index: number) => {
                    return (
                      <TemplateContainer key={`${index}-${template?.id}`}>
                        <AccordionWrapper>
                          <Accordion expanded={expanded === index} onChange={handleChange(index)} key={template?.id}>
                            <AccordionSummary
                              expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
                              aria-controls='panel1a-content'
                              id='panel1a-header'
                            >
                              <Stack
                                spacing={1}
                                direction='row'
                                alignItems='center'
                                justifyContent='space-between'
                                sx={{ width: "100%" }}
                              >
                                <div>
                                  <Typography>Hijo {index + 1}</Typography>
                                </div>
                              </Stack>
                            </AccordionSummary>

                            <AccordionDetails>
                              <div key={template?.id}>
                                <FieldTextField
                                  visible={true}
                                  label='Nombre'
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.name}`}
                                />
                                <FieldDatePicker
                                  subSectionId={index + 1}
                                  label='Fecha de nacimiento'
                                  name={`template.${index}.${templateChildren?.dateBirth}`}
                                />
                                <FieldSelect
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.gender}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[2]?.source}
                                  label='Género'
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Vive con usted en el hogar?'
                                  name={`template.${index}.${templateChildren?.livingPlace}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[3]}
                                />
                                <FieldSelect
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.takeCare}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[4]?.source}
                                  label='¿Quién cuida de su hijo la mayor parte del tiempo mientras usted trabaja?'
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Presenta discapacidad?'
                                  name={`template.${index}.${templateChildren?.hasDisability}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[5]}
                                />
                                <FieldTextField
                                  label='¿Cual?'
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.disabilityType}`}
                                  visible={
                                    formik?.values?.template?.[index]?.[`${templateChildren?.hasDisability}`] === YES
                                  }
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Aporta económicamente al hogar?'
                                  name={`template.${index}.${templateChildren?.isContributor}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[7]}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Depende económicamente del trabajador?'
                                  name={`template.${index}.${templateChildren?.isDependent}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[8]}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Recibe subsidio familiar?'
                                  name={`template.${index}.${templateChildren?.isBenefit}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[9]}
                                />
                                <FieldSelect
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.educationLevel}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[10]?.source}
                                  label='Último nivel educativo aprobado'
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Está actualmente estudiando?'
                                  onChangeCallback={(value: any) =>
                                    clearFieldsConditioned(
                                      `template.${index}.${templateChildren?.transportation}`,
                                      value
                                    )
                                  }
                                  name={`template.${index}.${templateChildren?.isStudent}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[11]}
                                />
                                <FieldCheckboxGroup
                                  isVisible={
                                    formik?.values?.template?.[index]?.[`${templateChildren?.isStudent}`] === YES
                                  }
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.transportation}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[12]?.source}
                                  label='¿Qué medio de transporte utiliza para desplazarse a su institución educativa?'
                                  checkboxStyle={{
                                    minWidth: "200px",
                                    margin: "0 1rem",
                                  }}
                                />

                                <FieldCheckboxGroup
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.estates}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[13]?.source}
                                  label='Seleccione los bienes que posee su hijo'
                                  checkboxStyle={{
                                    minWidth: "200px",
                                    margin: "0 1rem",
                                  }}
                                />

                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Participa en actividades extracurriculares?'
                                  name={`template.${index}.${templateChildren?.hasActivities}`}
                                  dataSource={questions[2]?.template[0]?.subQuestions[14]}
                                />
                                <FieldTextField
                                  label='Cuales'
                                  margin='normal'
                                  maxLength={100}
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templateChildren?.activity}`}
                                  visible={
                                    formik?.values?.template?.[index]?.[`${templateChildren?.hasActivities}`] === YES
                                  }
                                />
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        </AccordionWrapper>
                        {questions?.length > 0 ? (
                          <Delete>
                            <IconButton aria-label='delete' onClick={() => remove(index)}>
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </Delete>
                        ) : null}
                      </TemplateContainer>
                    );
                  })}
                  {form?.values?.template.length > 0 && form?.errors?.template?.length > 0 && (
                    <ErrorMessage margin='0'>
                      Debe registrar la información completa de cada uno de sus hijos
                    </ErrorMessage>
                  )}
                  <AddButtonWrapper>
                    <Button
                      variant='outlined'
                      onClick={() => push(templateChildrenModel)}
                      disabled={formik?.values?.template?.length === 25}
                    >
                      Agregar Hijo
                    </Button>
                  </AddButtonWrapper>
                </>
              )
            );
          }}
        />
      </StepContent>
    </Container>
  );
});

const Container = styled("div")`
  padding-left: 2rem;
`;

const TemplateContainer = styled("div")`
  flex: 1;
  display: flex;
  //padding-left: 1.5rem;
  align-items: center;
  flex-direction: row;
`;

const AddButtonWrapper = styled("div")`
  padding: 0.5rem 0;
`;

const AccordionWrapper = styled("div")`
  width: 100%;
`;

const Delete = styled("div")`
  right: 40px;
  margin-top: 5px;
  position: sticky;
  align-self: flex-start;
`;

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `0.1px solid ${theme.palette.primary.main}`,
    margin: "1px",
    // "&:not(:last-child)": {
    //   borderBottom: 0,
    // },
    "&:before": {
      display: "none",
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />} {...props} />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

export default memo(Step12);
